/**
 * Iter 145bh — Quantum side-by-side comparison badge.
 * Iter 145bl — Now also surfaces calibration shift + Kelly stake when
 * the auto-learning loop has shrunk/boosted Quantum's raw confidence.
 *
 * Renders Quantum's per-(fixture, market) pick alongside Classic, with
 * a HIT / MISS / VOID / PENDING outcome chip so users get full
 * transparency: same fixture, same market, two engines, two opinions.
 *
 * Usage:
 *   const lookup = useQuantumComparison(items);
 *   <QuantumCompareBadge q={lookup.get(fid, mt)} />
 *
 * Items array shape:
 *   [{ fixture_id: number, market_type: string }, ...]
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Atom, CheckCircle2, XCircle, Clock as ClockIcon, MinusCircle } from "lucide-react";

const _outcomeChip = (o) => {
  if (o === "hit") return { Icon: CheckCircle2, cls: "text-emerald-300 bg-emerald-500/15 border-emerald-500/40", text: "HIT" };
  if (o === "miss") return { Icon: XCircle, cls: "text-rose-300 bg-rose-500/15 border-rose-500/40", text: "MISS" };
  if (o === "void") return { Icon: MinusCircle, cls: "text-slate-300 bg-slate-500/15 border-slate-500/40", text: "VOID" };
  return { Icon: ClockIcon, cls: "text-muted-foreground bg-foreground/5 border-border", text: "PENDING" };
};

// Stable JSON key for memoising the batch payload signature.
const _sig = (items) =>
  (items || [])
    .map((i) => `${i.fixture_id}|${(i.market_type || "").toLowerCase()}`)
    .sort()
    .join("\n");

/**
 * Hook that batches up to ~200 (fixture, market) requests into one
 * POST /api/quantum/compare-batch call and returns a lookup function.
 *
 * Re-fires only when the underlying item signature changes.
 */
export function useQuantumComparison(items) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const lastSigRef = useRef("");

  useEffect(() => {
    const sig = _sig(items);
    if (!sig || sig === lastSigRef.current) return;
    lastSigRef.current = sig;
    const ctrl = new AbortController();
    setLoading(true);
    (async () => {
      try {
        const payload = (items || []).slice(0, 250).map((i) => ({
          fixture_id: Number(i.fixture_id),
          market_type: String(i.market_type || ""),
        }));
        const { data } = await axios.post(
          `${API}/quantum/compare-batch`,
          { items: payload },
          { timeout: 20000, signal: ctrl.signal }
        );
        setData(data?.quantum || {});
      } catch (_e) {
        // Non-fatal — badge just won't render.
        setData({});
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
    // We deliberately depend on the structural signature of `items` so
    // the effect doesn't re-fire on every parent re-render that creates
    // a new array reference with the same contents.
  }, [_sig(items)]);

  return {
    loading,
    get: (fid, mt) => {
      if (!data || fid == null || !mt) return null;
      const f = data[String(fid)];
      if (!f) return null;
      return f[mt] || null;
    },
  };
}

/**
 * One-line side-by-side Quantum badge.  Pass the result of
 * `lookup.get(fid, market_type)`.  Returns null if no Quantum pick
 * exists for this (fixture, market) tuple.
 */
export default function QuantumCompareBadge({ q, compact = false, testid }) {
  if (!q || !q.prediction) return null;
  const chip = _outcomeChip(q.outcome);
  const Icon = chip.Icon;
  // Iter 145bl — Show "raw → calibrated" when auto-learning meaningfully shifted the pick.
  const hasShift = q.calibration_pp != null && Math.abs(q.calibration_pp) >= 1.0;
  const kelly = q.kelly_stake_pct;
  const hasKelly = kelly != null && kelly > 0;
  return (
    <div
      data-testid={testid}
      className={`mt-2 rounded-lg border border-cyan-500/30 bg-cyan-500/5 px-2 py-1.5 ${compact ? "text-[10px]" : "text-[11px]"}`}
      title={`Quantum Engine pick · ${q.confidence ?? "?"}% confidence${
        hasShift ? ` (calibrated from raw ${q.raw_confidence}%)` : ""
      }${hasKelly ? ` · Kelly ${kelly}%` : ""}`}
    >
      <div className="flex items-center gap-2">
        <Atom className={`${compact ? "w-3 h-3" : "w-3.5 h-3.5"} text-cyan-400 shrink-0`} />
        <span className="text-[9px] uppercase tracking-[0.16em] font-extrabold text-cyan-300/90 shrink-0">
          Quantum
        </span>
        <span className="font-semibold text-foreground/90 truncate flex-1 min-w-0">
          {q.prediction}
        </span>
        <span className="font-bold tabular-nums text-cyan-200 shrink-0">
          {q.confidence != null ? `${q.confidence}%` : "—"}
        </span>
        <span
          className={`inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-[0.14em] px-1 py-0.5 rounded border ${chip.cls} shrink-0`}
        >
          <Icon className="w-2.5 h-2.5" />
          {chip.text}
        </span>
      </div>
      {(hasShift || hasKelly) && (
        <div className="mt-1 ml-5 flex items-center gap-2 text-[9px] text-muted-foreground">
          {hasShift && (
            <span
              className={`tabular-nums font-bold ${q.calibration_pp < 0 ? "text-amber-300/80" : "text-emerald-300/80"}`}
              title="Auto-learning shift: how much calibration buckets shrunk (negative) or boosted (positive) the raw model confidence"
            >
              raw {q.raw_confidence}% → cal{" "}
              {q.calibration_pp > 0 ? "+" : ""}{q.calibration_pp}pp
            </span>
          )}
          {hasKelly && (
            <span
              className="tabular-nums font-bold text-emerald-300/90"
              title="Quarter-Kelly bankroll fraction based on calibrated edge"
            >
              Kelly {kelly}%
            </span>
          )}
        </div>
      )}
    </div>
  );
}
